import {sweetAPI} from 'app/store/sweet-api';
import {
  providesTagsByURL,
  showLoadingBar,
  showMessages,
} from 'app/store/sweet-api/utils';
import {COMMENTS_API} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {FilterContentType} from './types';

import {
  CommentableContentType,
  CommentSerializedType,
  ListResponseType,
} from 'sweet-types';

export const COMMENT_TAG = 'comment';

export type CommentFetchType = {
  object_pk: number;
  content_type: FilterContentType;
  version_url?: string;
  exclude_deleted_comments?: string;
  page?: string | number;
};

export type addCommentProps = {
  object_pk: number;
  parentId: number;
  content_type: CommentableContentType;
  comment: string;
};

export const extendedAPI = sweetAPI
  .enhanceEndpoints({addTagTypes: [COMMENT_TAG]})
  .injectEndpoints({
    endpoints: (build) => ({
      comments: build.query<
        ListResponseType<CommentSerializedType>,
        CommentFetchType
      >({
        query: (params) => ({
          url: COMMENTS_API,
          params: params,
        }),
        providesTags: providesTagsByURL(COMMENT_TAG),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: {
              fallback: (response) =>
                response?.message ?? gt('Unable to load comments.'),
              byStatus: {},
              silenceByStatus: {404: true},
            },
          })(args, api);
        },
      }),
      addComment: build.mutation<unknown, addCommentProps>({
        query: (body) => ({
          url: COMMENTS_API,
          method: 'POST',
          body,
        }),
        invalidatesTags: () => [{type: COMMENT_TAG, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to add comment.'),
            onSuccess: () => gt('Comment added successfully.'),
          })(args, api);
        },
      }),
      deleteComment: build.mutation<unknown, {url: string}>({
        query: ({url}) => ({
          url,
          method: 'DELETE',
        }),
        invalidatesTags: () => [{type: COMMENT_TAG, id: 'list'}],
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to delete comment.'),
            onSuccess: () => gt('Comment was deleted successfully.'),
          })(args, api);
        },
      }),
    }),
  });

// https://redux-toolkit.js.org/rtk-query/usage/queries#hook-types
export const {
  useCommentsQuery: useComments,
  useAddCommentMutation: useAddComment,
  useDeleteCommentMutation: useDeleteComment,
} = extendedAPI;
